import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ChevronDown, CircleCheck, LayoutGrid, Lock, Wallet } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { clearCart } from '@/features/cart/cartSlice';
import { BillHero } from '@/features/cart/BillHero';
import api, { getErrorMessage, isNetworkError } from '@/services/api';
import { Alert, Button, Chip, IconButton, Input, MethodTile, NumericKeypad, Overline, Sheet, SummaryLine } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import {
    CheckoutRequest,
    CheckoutResponse,
    ApiResponse,
    PaymentMethod,
    PaymentAccount,
    BankCode,
    EPaymentMethod,
    StockValidation,
    StockValidationItemRequest,
    TransactionDetail,
} from '@/services/types';
import { API_ENDPOINTS, BANK_CODES } from '@/utils/constants';
import { suggestCash } from '@/utils/cashSuggestions';
import { formatCurrency, toPaymentMethod } from '@/utils/formatters';
import { tagNotes } from '@/utils/orderType';
import { calcPaymentFee } from '@/utils/paymentFee';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { enqueueSale } from './checkoutQueue';
import { PendingPaymentView } from './PendingPaymentView';
import { SaleCompleteView } from './SaleCompleteView';
import { distributeDiscount } from '@/utils/discountAllocator';

interface Tile { key: string; label: string; icon?: React.ReactNode; img?: string | null; selected: boolean; onSelect: () => void }

/**
 * 06 Pembayaran. Phone: T3 (bill hero → methods → account/cash → sticky CTA).
 * Landscape (design 06): 360px order summary | 320px method rows + inline account radios | cash input + keypad.
 * Payment logic (guards, idempotency key, queue, stock validation, checkout) is unchanged.
 */
const CheckoutPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { selectedShop, user, shopConfig } = useAppSelector((state) => state.auth);
    const { items, calculation, customerName, customerPhone, notes, totalDiscount, orderType } = useAppSelector((state) => state.cart);
    // clearCart() nulls the server calculation; keep the last one so the result screen can render
    const calcRef = useRef(calculation);
    if (calculation) calcRef.current = calculation;
    const calc = calcRef.current;
    const itemsRef = useRef(items);
    if (items.length > 0) itemsRef.current = items;

    const [paymentType, setPaymentType] = useState<'epayment-method' | 'rwallet' | null>(null);
    const [selectedEPaymentMethod, setSelectedEPaymentMethod] = useState<EPaymentMethod | null>(null);
    const [ePaymentMethods, setEPaymentMethods] = useState<EPaymentMethod[]>([]);
    const [loadingEPaymentMethods, setLoadingEPaymentMethods] = useState(false);
    const [selectedPaymentAccount, setSelectedPaymentAccount] = useState<string>('');
    const [paymentAccounts, setPaymentAccounts] = useState<PaymentAccount[]>([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [cashReceived, setCashReceived] = useState<number>(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<CheckoutResponse | 'queued' | null>(null);
    const [sheet, setSheet] = useState<'methods' | 'account' | null>(null);

    // Redirect if cart is empty (unless we just finished a sale and are showing the result)
    useEffect(() => {
        if (items.length === 0 && result === null) navigate('/catalog');
    }, [items, navigate, result]);

    // Clear the cart only once `result` is committed. Dispatching it next to setResult after an await
    // lets the SyncLane store render land first (items empty, result still null) and the redirect above fires.
    useEffect(() => {
        if (result !== null) dispatch(clearCart());
    }, [result, dispatch]);

    useEffect(() => {
        if (user && selectedShop) {
            fetchEPaymentMethods();
            fetchPaymentAccounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, selectedShop]);

    useEffect(() => {
        if (paymentType !== null || loadingAccounts || loadingEPaymentMethods) return;
        if (paymentAccounts.length > 0) setPaymentType('rwallet');
        else if (ePaymentMethods.length > 0) setPaymentType('epayment-method');
    }, [paymentType, loadingAccounts, loadingEPaymentMethods, paymentAccounts, ePaymentMethods]);

    const fetchEPaymentMethods = async () => {
        if (!user || !selectedShop) return;
        setLoadingEPaymentMethods(true);
        try {
            const response = await api.get<ApiResponse<EPaymentMethod[]>>(
                API_ENDPOINTS.PAYMENT_METHODS_LIST(user.usersId, selectedShop.shopId),
                { params: { identifier: 'PAYMENT_TYPE_UPDATE' } }
            );
            setEPaymentMethods(response.data.data);
            if (response.data.data.length > 0) setSelectedEPaymentMethod(response.data.data[0]);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoadingEPaymentMethods(false);
        }
    };

    const fetchPaymentAccounts = async () => {
        if (!selectedShop) return;
        setLoadingAccounts(true);
        try {
            const response = await api.get<ApiResponse<PaymentAccount[]>>(API_ENDPOINTS.PAYMENT_ACCOUNTS(selectedShop.shopId));
            setPaymentAccounts(response.data.data);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoadingAccounts(false);
        }
    };

    const { paymentFee, finalTotal } = useMemo(() => {
        if (!calc) return { paymentFee: 0, finalTotal: 0 };
        const fee = paymentType === 'epayment-method' ? calcPaymentFee(selectedEPaymentMethod, calc.grandTotal) : 0;
        return { paymentFee: fee, finalTotal: calc.grandTotal + fee };
    }, [calc, paymentType, selectedEPaymentMethod]);

    const { availableAccounts, insufficientAccounts } = useMemo(() => ({
        availableAccounts: paymentAccounts.filter((a) => a.balance >= finalTotal),
        insufficientAccounts: paymentAccounts.filter((a) => a.balance < finalTotal),
    }), [paymentAccounts, finalTotal]);

    useEffect(() => {
        if (paymentType === 'rwallet' && availableAccounts.length > 0) {
            if (!availableAccounts.some((a) => a.identifier === selectedPaymentAccount)) {
                const main = availableAccounts.find((a) => a.isMainAccount);
                setSelectedPaymentAccount((main ?? availableAccounts[0]).identifier);
            }
        } else if (paymentType === 'rwallet') {
            setSelectedPaymentAccount('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableAccounts, paymentType]);

    const change = Math.max(0, cashReceived - finalTotal);
    const shortfall = cashReceived > 0 ? Math.max(0, finalTotal - cashReceived) : 0;

    if (!selectedShop || !calc) return null;
    if (items.length === 0 && result === null) return null;

    const finishSale = () => {
        setResult(null);
        navigate('/catalog');
    };

    const handleCheckout = async () => {
        setError(null);

        if (calc.maxDiscount != null && totalDiscount > calc.maxDiscount) {
            setError(t('checkout.discountExceedsLimit'));
            return;
        }

        const itemDiscounts = distributeDiscount(items, totalDiscount);

        // Per-line discount cap (server-provided)
        const over = items.findIndex((item, idx) => {
            const cap = calc.items.find((c) => c.compositeId === item.compositeId)?.maxItemDiscount;
            return cap != null && itemDiscounts[idx] > cap;
        });
        if (over >= 0) {
            setError(t('checkout.itemDiscountExceeds', { name: items[over].name }));
            return;
        }

        if (shopConfig?.requireCustomerInfo && (!customerName.trim() || !customerPhone.trim())) {
            setError(t('checkout.customerInfoRequired'));
            return;
        }

        let resolvedPaymentMethod: PaymentMethod;
        let resolvedBankCode: BankCode;
        if (paymentType === 'epayment-method') {
            if (!selectedEPaymentMethod) { setError(t('checkout.noPaymentMethods')); return; }
            resolvedPaymentMethod = toPaymentMethod(selectedEPaymentMethod.paymentType);
            resolvedBankCode = selectedEPaymentMethod.paymentCode as BankCode;
        } else {
            if (!selectedPaymentAccount) { setError(t('checkout.paymentAccountRequired')); return; }
            const acc = availableAccounts.find((a) => a.identifier === selectedPaymentAccount);
            if (!acc || acc.balance < finalTotal) { setError(t('checkout.insufficientBalance')); return; }
            resolvedPaymentMethod = 'EPAYMENT';
            resolvedBankCode = BANK_CODES.EPAYMENT;
        }

        setIsProcessing(true);
        try {
            const checkoutData: CheckoutRequest = {
                idempotencyKey: uuidv4(),
                items: items.map((item, idx) => ({
                    compositeId: item.compositeId,
                    quantity: item.quantity,
                    discount: itemDiscounts[idx],
                    notes: item.notes,
                    prescriptionRef: item.prescriptionRef,
                    doctorName: item.doctorName,
                    patientName: item.patientName,
                    dosageInstructions: item.dosageInstructions,
                    lineTotal: item.lineTotal,
                    inStock: item.inStock,
                    unitPrice: item.unitPrice,
                    taxAmount: item.taxAmount,
                    isTaxable: item.isTaxable,
                    availableQty: item.availableQty,
                })),
                payment: {
                    method: resolvedPaymentMethod,
                    bankCode: resolvedBankCode,
                    amount: Math.round(finalTotal),
                    paymentAccountIdentifier: paymentType === 'rwallet' ? selectedPaymentAccount : undefined,
                    paymentFee,
                },
                customerName,
                customerPhone,
                // Backend has no order-type field: it rides as a "[TAG]" prefix in notes
                notes: tagNotes(orderType, notes),
            };

            if (paymentType === 'rwallet') {
                // Fire-and-forget: queue, clear cart, keep selling. Retried in background with backoff.
                enqueueSale({
                    id: checkoutData.idempotencyKey,
                    shopId: selectedShop.shopId,
                    request: checkoutData,
                    cart: { items, customerName, customerPhone, notes, orderType, totalDiscount },
                });
                setResult('queued');
                toast.success(t('queue.saleQueued'));
                return;
            }

            // Stock pre-validation (network errors ignored: server re-validates)
            try {
                const body: StockValidationItemRequest[] = items.map((i) => ({ compositeId: i.compositeId, quantity: i.quantity }));
                const v = await api.post<ApiResponse<StockValidation>>(API_ENDPOINTS.VALIDATE_STOCK(selectedShop.shopId), body);
                if (!v.data.data.available) {
                    const short = v.data.data.items.filter((i) => !i.sufficient).map((i) => `${i.name} (${i.available})`).join(', ');
                    setError(v.data.data.message || t('checkout.stockInsufficient', { items: short }));
                    return;
                }
            } catch (e) {
                if (!isNetworkError(e)) throw e;
            }

            const response = await api.post<ApiResponse<CheckoutResponse>>(
                API_ENDPOINTS.CHECKOUT(selectedShop.shopId),
                checkoutData
            );
            setResult(response.data.data);
            toast.success(t('checkout.transactionSuccess'));
        } catch (err) {
            setError(getErrorMessage(err));
            toast.error(t('checkout.transactionFailed'));
        } finally {
            setIsProcessing(false);
        }
    };

    const pollPaid = async (receiptNumber: string) => {
        const res = await api.get<ApiResponse<TransactionDetail>>(API_ENDPOINTS.TRANSACTION_DETAILS(selectedShop.shopId, receiptNumber));
        const status = res.data.data.invoice?.status;
        return status != null && status !== 'WAITINGFORPAYMENT';
    };

    const isCheckoutDisabled =
        paymentType === null ||
        (paymentType === 'epayment-method' && (!selectedEPaymentMethod || loadingEPaymentMethods)) ||
        (paymentType === 'rwallet' && (!selectedPaymentAccount || availableAccounts.length === 0 || loadingAccounts));

    const pendingUrl = result && result !== 'queued' && result.invoice?.paymentUrl ? result.invoice.paymentUrl : null;
    const selectedAccount = availableAccounts.find((a) => a.identifier === selectedPaymentAccount);
    const methodLabel = paymentType === 'rwallet' ? ['rWallet', selectedAccount?.accountName].filter(Boolean).join(' · ') : selectedEPaymentMethod?.paymentName;
    const orderTypeLabel = t(`cart.orderTypes.${orderType}` as 'cart.orderTypes.DINE_IN');
    const shownItems = itemsRef.current;

    // ---- Result screens ----
    if (result !== null && !pendingUrl) {
        return (
            <SaleCompleteView
                result={result}
                shopId={selectedShop.shopId}
                methodLabel={methodLabel}
                cashReceived={cashReceived}
                change={change}
                onNewSale={finishSale}
            />
        );
    }

    const summaryLines = (
        <div className="flex flex-col gap-3 text-[15px] tnum">
            {shownItems.map((item) => (
                <div key={item.compositeId} className="flex justify-between gap-3"><span className="truncate">{item.quantity}× {item.name}</span><span className="font-bold">{formatCurrency((item.unitPrice || 0) * item.quantity)}</span></div>
            ))}
            <div className="border-t border-line-subtle" />
            {calc.discountTotal > 0 && <SummaryLine kind="discount" label={t('cart.discount')} value={`−${formatCurrency(calc.discountTotal)}`} />}
            <SummaryLine label={calc.taxRate ? `${t('cart.tax')} ${calc.taxRate}%` : t('cart.tax')} value={formatCurrency(calc.taxTotal)} />
            {calc.serviceChargeTotal > 0 && <SummaryLine label={t('cart.serviceCharge')} value={formatCurrency(calc.serviceChargeTotal)} />}
            {paymentFee > 0 && <SummaryLine label={`${selectedEPaymentMethod?.paymentName ?? ''} ${t('transactions.paymentFee')}`.trim()} value={formatCurrency(paymentFee)} />}
        </div>
    );

    if (pendingUrl && result !== 'queued' && result) {
        return (
            <PendingPaymentView
                isOpen
                paymentUrl={pendingUrl}
                qrString={result.invoice?.qrString}
                invoiceIdentifier={result.invoice?.invoiceIdentifier}
                poll={() => pollPaid(result.receiptNumber)}
                onClose={finishSale}
                amount={result.grandTotal}
                methodName={selectedEPaymentMethod?.paymentName}
                summary={summaryLines}
            />
        );
    }

    // ---- Method tiles (rWallet first, then API order = most used) ----
    const tiles: Tile[] = [
        ...(paymentAccounts.length > 0 ? [{
            key: 'rwallet', label: 'rWallet', icon: <Wallet />, selected: paymentType === 'rwallet',
            onSelect: () => { setPaymentType('rwallet'); setSelectedEPaymentMethod(null); },
        }] : []),
        ...ePaymentMethods.map((m) => ({
            key: m.paymentCode, label: m.paymentName, img: m.paymentImage,
            selected: paymentType === 'epayment-method' && selectedEPaymentMethod?.paymentCode === m.paymentCode,
            onSelect: () => { setPaymentType('epayment-method'); setSelectedEPaymentMethod(m); },
        })),
    ];
    let visible = tiles;
    let hidden: Tile[] = [];
    if (tiles.length > 4) {
        const head = tiles.slice(0, 3);
        const sel = tiles.find((x) => x.selected);
        visible = sel && !head.includes(sel) ? [sel, ...head.slice(0, 2)] : head;
        hidden = tiles.filter((x) => !visible.includes(x));
    }

    const accountStrip = paymentType === 'rwallet' && (
        loadingAccounts ? <p className="text-sm text-ink-muted">{t('common.loading')}</p>
        : availableAccounts.length === 0 ? (
            <Alert variant="warning" message={t('checkout.noSufficientBalance')} action={<Button variant="outline" size="sm" onClick={() => navigate('/topup')}>{t('topup.title')}</Button>} />
        ) : (
            <button type="button" onClick={() => setSheet('account')} className="flex h-[52px] items-center gap-2.5 rounded-md bg-brandsoft px-3.5 text-left text-ink-brand lg:h-[58px] lg:px-4">
                <span className="text-xs font-bold text-ink-secondary lg:text-[13px]">{t('checkout.account')}</span>
                <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-black lg:text-[15px]">{selectedAccount?.accountName ?? t('checkout.selectPaymentAccount')}</span>
                    {selectedAccount && <span className="text-[11px] font-bold text-ink-secondary tnum lg:text-xs">{t('checkout.balance')} {formatCurrency(selectedAccount.balance)}</span>}
                </span>
                <span className="text-[13px] font-extrabold lg:text-sm">{t('checkout.switchAccount')}</span>
                <ChevronDown className="h-4 w-4" />
            </button>
        )
    );

    const accountPanel = paymentType === 'rwallet' && (
        <div className="-mt-0.5 mb-1 flex flex-col gap-1.5 rounded-md bg-surface-sunken p-2">
            {loadingAccounts ? <p className="px-2 py-1 text-sm text-ink-muted">{t('common.loading')}</p>
            : availableAccounts.length === 0 ? (
                <Alert variant="warning" message={t('checkout.noSufficientBalance')} action={<Button variant="outline" size="sm" onClick={() => navigate('/topup')}>{t('topup.title')}</Button>} />
            ) : availableAccounts.map((a) => {
                const active = a.identifier === selectedPaymentAccount;
                return (
                    <button key={a.id} type="button" role="radio" aria-checked={active} onClick={() => setSelectedPaymentAccount(a.identifier)} className={clsx('flex h-[52px] items-center gap-2.5 rounded-md px-3 text-left', active ? 'border-2 border-primary-500 bg-surface-card text-ink-brand' : 'border border-line-subtle')}>
                        <span className={clsx('h-[18px] w-[18px] shrink-0 rounded-full bg-surface-card', active ? 'border-[5px] border-primary-500' : 'border-2 border-line')} />
                        <span className="flex min-w-0 flex-1 flex-col">
                            <span className={clsx('truncate text-sm', active ? 'font-black' : 'font-extrabold')}>{a.accountName}</span>
                            <span className="text-xs font-bold text-ink-secondary tnum">{t('checkout.balance')} {formatCurrency(a.balance)} · {a.accountNumber}</span>
                        </span>
                    </button>
                );
            })}
            {insufficientAccounts.map((a) => (
                <div key={a.id} className="flex h-[52px] items-center gap-2.5 rounded-md border border-line-subtle px-3 text-ink-muted">
                    <Lock className="h-[18px] w-[18px] shrink-0" />
                    <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-extrabold">{a.accountName}</span><span className="text-xs font-bold tnum">{t('checkout.balance')} {formatCurrency(a.balance)} · {t('checkout.insufficient')}</span></span>
                </div>
            ))}
        </div>
    );

    const feeInfo = paymentType === 'epayment-method' && selectedEPaymentMethod && (
        <>
            {paymentFee > 0 && (
                <Alert variant="warning" message={selectedEPaymentMethod.paymentFeeUnit === 'PERCENT' ? t('checkout.feePercent', { fee: selectedEPaymentMethod.paymentFee }) : t('checkout.feeAmount', { fee: formatCurrency(selectedEPaymentMethod.paymentFee) })} />
            )}
            {selectedEPaymentMethod.paymentType.toLowerCase() === 'transfer' && selectedEPaymentMethod.howToPay && (
                <Alert variant="brand" title={t('checkout.howToPay')} message={<span className="whitespace-pre-line">{selectedEPaymentMethod.howToPay}</span>} />
            )}
        </>
    );

    // Cash received / change: client-side aid, nothing is sent to the server (Android shows it for every method)
    const cashBlock = paymentType !== null && (
        <div className="flex flex-col gap-3">
            <span className="text-[15px] font-extrabold">{t('checkout.cashReceived')}</span>
            <Input
                fieldSize="lg"
                numeric
                type="number"
                min={0}
                step={1000}
                inputMode="numeric"
                placeholder="0"
                value={cashReceived || ''}
                onChange={(e) => setCashReceived(Math.max(0, parseFloat(e.target.value) || 0))}
                leftIcon={<span className="text-xl font-extrabold text-ink-muted">Rp</span>}
                wrapperClassName="!h-16 !border-2 !border-accent-500 shadow-focus lg:!h-[68px]"
                className="!text-[28px] lg:!text-[32px]"
            />
            <div className="flex flex-wrap gap-2">
                <Chip selected={cashReceived === Math.round(finalTotal)} onClick={() => setCashReceived(Math.round(finalTotal))}>{t('checkout.exactAmount')}</Chip>
                {suggestCash(finalTotal).map((amt) => (
                    <Chip key={amt} selected={cashReceived === amt} onClick={() => setCashReceived(amt)}>{formatCurrency(amt)}</Chip>
                ))}
                {cashReceived > 0 && <Chip onClick={() => setCashReceived(0)}>{t('common.reset')}</Chip>}
            </div>
        </div>
    );

    const changeBanner = cashReceived > 0 && (
        <div className={clsx('flex items-center justify-between rounded-lg px-[18px] py-4', shortfall > 0 ? 'bg-status-danger-soft text-danger-700' : 'bg-status-success-soft text-success-700')}>
            <span className="text-[15px] font-extrabold">{shortfall > 0 ? t('checkout.shortfall') : t('checkout.change')}</span>
            <span className="text-[30px] font-black tnum lg:text-[34px]">{formatCurrency(shortfall > 0 ? shortfall : change)}</span>
        </div>
    );

    const methodsHeader = (
        <div className="flex items-baseline justify-between">
            <span className="text-[15px] font-extrabold">{t('checkout.paymentMethod')}</span>
            <span className="text-xs text-ink-muted">{t('checkout.methodOrderHint')}</span>
        </div>
    );
    const methodGrid = (layout: 'stacked' | 'inline') => (
        loadingEPaymentMethods ? <p className="text-sm text-ink-muted">{t('checkout.loadingPaymentMethods')}</p>
        : tiles.length === 0 ? <Alert variant="warning" message={t('checkout.noPaymentMethods')} />
        : (
            <div className={clsx('grid grid-cols-4', layout === 'stacked' ? 'gap-2' : 'gap-3')}>
                {visible.map((x) => <MethodTile key={x.key} layout={layout} label={x.label} icon={x.icon} img={x.img} selected={x.selected} onClick={x.onSelect} />)}
                {hidden.length > 0 && <MethodTile layout={layout} label={t('checkout.more')} icon={<LayoutGrid />} more={hidden.length} onClick={() => setSheet('methods')} />}
            </div>
        )
    );

    const cta = (
        <Button variant="primary" size="lg" fullWidth onClick={handleCheckout} loading={isProcessing} disabled={isCheckoutDisabled}>
            {t('checkout.completeSale')}
        </Button>
    );

    return (
        <>
            {/* ---- Phone: T3 ---- */}
            <div className="flex h-full min-h-0 flex-1 flex-col lg:hidden">
                <PageShell title={t('checkout.title')} backTo="/cart" footer={cta}>
                    <div className="flex flex-col gap-4 pb-4">
                        <BillHero amount={finalTotal} itemCount={shownItems.reduce((n, i) => n + i.quantity, 0)} orderTypeLabel={orderTypeLabel} customer={customerName || undefined} />
                        {error && <Alert variant="error" message={error} />}
                        {methodsHeader}
                        {methodGrid('stacked')}
                        {accountStrip}
                        {feeInfo}
                        {cashBlock}
                        {changeBanner}
                    </div>
                </PageShell>
            </div>

            {/* ---- Landscape (06): summary | method rows | cash + keypad ---- */}
            <div className="hidden h-full min-h-0 flex-1 lg:flex">
                <aside className="flex w-[360px] shrink-0 flex-col gap-[18px] overflow-y-auto border-r border-line-subtle bg-surface-card p-6">
                    <div className="flex items-center gap-3">
                        <IconButton variant="sunken" label={t('common.back')} onClick={() => navigate('/cart')}><ArrowLeft /></IconButton>
                        <h1 className="text-[22px] font-black">{t('checkout.title')}</h1>
                    </div>
                    {summaryLines}
                    <div className="flex-1" />
                    <BillHero large amount={finalTotal} itemCount={shownItems.reduce((n, i) => n + i.quantity, 0)} orderTypeLabel={orderTypeLabel} customer={[customerName, notes].filter(Boolean).join(', ') || undefined} />
                </aside>
                <div className="flex w-[320px] shrink-0 flex-col gap-2.5 overflow-y-auto border-r border-line-subtle p-[24px_20px]">
                    <div className="mb-1 flex flex-col gap-0.5">
                        <span className="text-[15px] font-extrabold">{t('checkout.paymentMethod')}</span>
                        <span className="text-xs text-ink-muted">{t('checkout.methodOrderHint')}</span>
                    </div>
                    {loadingEPaymentMethods ? <p className="text-sm text-ink-muted">{t('checkout.loadingPaymentMethods')}</p>
                    : tiles.length === 0 ? <Alert variant="warning" message={t('checkout.noPaymentMethods')} />
                    : visible.map((x) => (
                        <React.Fragment key={x.key}>
                            <MethodTile layout="inline" className="shrink-0 !h-[60px]" label={x.label} icon={x.icon} img={x.img} selected={x.selected} onClick={x.onSelect} />
                            {x.selected && (x.key === 'rwallet' ? accountPanel : feeInfo)}
                        </React.Fragment>
                    ))}
                    {hidden.length > 0 && <MethodTile layout="inline" className="shrink-0 !h-[60px]" label={t('checkout.more')} icon={<LayoutGrid />} more={hidden.length} onClick={() => setSheet('methods')} />}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-[24px_28px]">
                    {error && <Alert variant="error" message={error} />}
                    {cashBlock}
                    {paymentType !== null && (
                        <NumericKeypad className="min-h-0 flex-1" value={cashReceived ? String(cashReceived) : ''} onChange={(d) => setCashReceived(parseInt(d || '0', 10))} />
                    )}
                    {changeBanner}
                    {cta}
                </div>
            </div>

            {/* 06b — all methods */}
            <Sheet isOpen={sheet === 'methods'} onClose={() => setSheet(null)} title={t('checkout.allMethods')} titleAside={<span className="text-[15px] font-black tnum text-ink-brand">{formatCurrency(finalTotal)}</span>}>
                <div className="flex flex-col gap-2">
                    <Overline>{t('checkout.frequentlyUsed')}</Overline>
                    <div className="overflow-hidden rounded-lg border border-line-subtle">
                        {tiles.slice(0, 3).map((x) => <MethodRow key={x.key} tile={x} onPick={() => setSheet(null)} />)}
                    </div>
                    {tiles.length > 3 && (
                        <>
                            <Overline className="mt-2">{t('checkout.otherMethods')}</Overline>
                            <div className="overflow-hidden rounded-lg border border-line-subtle">
                                {tiles.slice(3).map((x) => <MethodRow key={x.key} tile={x} onPick={() => setSheet(null)} />)}
                            </div>
                        </>
                    )}
                </div>
            </Sheet>

            {/* 06c — rWallet account */}
            <Sheet
                isOpen={sheet === 'account'}
                onClose={() => setSheet(null)}
                title={t('checkout.chooseAccount')}
                footer={<Button variant="primary" size="lg" fullWidth onClick={() => setSheet(null)} disabled={!selectedAccount}>{selectedAccount ? t('checkout.useAccount', { name: selectedAccount.accountName }) : t('checkout.selectPaymentAccount')}</Button>}
            >
                <p className="mb-3 text-[13px] text-ink-secondary">{t('checkout.chooseAccountHint', { amount: formatCurrency(finalTotal) })}</p>
                <div className="flex flex-col gap-2">
                    {availableAccounts.map((a) => {
                        const active = a.identifier === selectedPaymentAccount;
                        return (
                            <button key={a.id} type="button" onClick={() => setSelectedPaymentAccount(a.identifier)} className={clsx('flex h-16 items-center gap-3 rounded-lg px-3.5 text-left', active ? 'border-2 border-primary-500 bg-brandsoft text-ink-brand' : 'border border-line bg-surface-card')}>
                                <span className={clsx('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', active ? 'bg-surface-card' : 'bg-surface-sunken')}><Wallet className="h-[19px] w-[19px]" /></span>
                                <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-black">{a.accountName}</span><span className="text-xs font-bold text-ink-secondary tnum">{t('checkout.balance')} {formatCurrency(a.balance)} · {a.accountNumber}</span></span>
                                {active && <CircleCheck className="h-5 w-5" />}
                            </button>
                        );
                    })}
                    {insufficientAccounts.map((a) => (
                        <div key={a.id} className="flex h-16 items-center gap-3 rounded-lg border border-line-subtle bg-surface-sunken px-3.5 text-ink-muted">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-card"><Lock className="h-[18px] w-[18px]" /></span>
                            <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-black">{a.accountName}</span><span className="text-xs font-bold tnum">{t('checkout.balance')} {formatCurrency(a.balance)} · {t('checkout.insufficient')}</span></span>
                        </div>
                    ))}
                </div>
            </Sheet>
        </>
    );
};

const MethodRow: React.FC<{ tile: Tile; onPick: () => void }> = ({ tile, onPick }) => (
    <button
        type="button"
        onClick={() => { tile.onSelect(); onPick(); }}
        className={clsx('flex h-14 w-full items-center gap-3 border-b border-line-subtle px-3.5 text-left last:border-b-0', tile.selected ? 'bg-brandsoft text-ink-brand' : 'bg-surface-card')}
    >
        <span className={clsx('inline-flex [&>svg]:h-[21px] [&>svg]:w-[21px]', !tile.selected && 'text-ink-secondary')}>
            {tile.img ? <img src={tile.img} alt="" className="h-[21px] w-[21px] object-contain" /> : tile.icon}
        </span>
        <span className={clsx('flex-1 text-sm', tile.selected ? 'font-black' : 'font-extrabold')}>{tile.label}</span>
        {tile.selected && <CircleCheck className="h-5 w-5" />}
    </button>
);

export default CheckoutPage;
